import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from '../../../../../contexts/AuthContext';
import { PermissionModulesEnum, PermissionActionsEnum } from '../../../../../enums';
import { hasResourseAccess } from '../../../../../utils/methods';
import useFetch from '../../../../../hooks/useFetch';
import TransactionService from '../../../../../services/transaction.service';
import ReservationService from '../../../../../services/reservation.service';
import InvoiceService from '../../../../../services/invoice.service';
import MonthStatCard from './MonthStatCard';
import CombinedInvoiceCard from './CombinedInvoiceCard';
import Spinner from '../../../../../components/Spinner';
import Dropdown from "../../../../../components/Dropdown";

interface CurrentMonthStatsProps {
  monthName: string;
  startDate: string;
  endDate: string;
  monthOptions: any[];
  currentMonthYear: string;
  onMonthYearChange: (e: any) => void;
}

const CurrentMonthStats: React.FC<CurrentMonthStatsProps> = ({ monthName, startDate, endDate, monthOptions, currentMonthYear, onMonthYearChange }) => {
  const { t } = useTranslation();
  const { permissions, isSystemAdmin } = useContext(AuthContext);

  // const canViewTransactions = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Transaction]: [PermissionActionsEnum.View] }] });
  // const canViewReservations = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Reservation]: [PermissionActionsEnum.View] }] });
  // const canViewInvoices = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Invoices]: [PermissionActionsEnum.View] }] });

  const canViewTransactions = true;
  const canViewReservations = true;
  const canViewInvoices = true;

  const [transactionKpis, setTransactionKpis] = useState({ unbilled: 0, invoiced: 0, settled: 0, total: 0 });
  const [reservationKpis, setReservationKpis] = useState({ unbilled: 0, invoiced: 0, settled: 0, total: 0 });
  const [invoiceFinancialKpis, setInvoiceFinancialKpis] = useState({
    totalAmountDue: 0,
    totalInvoiceAmount: 0,
    totalCommission: 0
  });
  const [invoiceKpis, setInvoiceKpis] = useState({ draft: 0, sent: 0, completed: 0 });

  const { total: totalTransactions, loading: loadingTransactions } = useFetch({
    service: TransactionService,
    func: 'list',
    payload: {
      page: 1,
      perPage: 1,
      orderBy: 'transaction_date',
      order: 'ASC',
      status: [],
      searchText: '',
      propertyId: '',
      transactionType: '',
      debitCredit: '',
      managedBy: '',
      reviewedBy: '',
      startDate,
      endDate
    },
    dependency: [startDate, endDate],
    skip: !canViewTransactions,
    onSuccess: (response: any) => {
      setTransactionKpis({
        unbilled: response.total_unbilled_count ?? 0,
        invoiced: response.total_invoiced_count ?? 0,
        settled: response.total_settled_count ?? 0,
        total: response.total ?? 0,
      });
    },
  });

  const { total: totalReservations, loading: loadingReservations } = useFetch({
    service: ReservationService,
    func: 'list',
    payload: {
      page: 1,
      perPage: 1,
      orderBy: 'check_in_date',
      order: 'ASC',
      searchText: '',
      property: '',
      platform: '',
      reservationStatus: '',
      status: '',
      source: '',
      managedBy: '',
      reviewedBy: '',
      startDate,
      endDate
    },
    dependency: [startDate, endDate],
    skip: !canViewReservations,
    onSuccess: (response: any) => {
      setReservationKpis({
        unbilled: response.total_unbilled_count ?? 0,
        invoiced: response.total_invoiced_count ?? 0,
        settled: response.total_settled_count ?? 0,
        total: response.total ?? 0,
      });
    },
  });

  const { loading: loadingInvoices } = useFetch({
    service: InvoiceService,
    func: 'list',
    payload: {
      page: 1,
      perPage: 1,
      orderBy: 'invoice_status',
      order: 'ASC',
      property: '',
      customer: '',
      invoiceStatus: '',
      createdBy: '',
      searchText: '',
      reviewedBy: '',
      startDate,
      endDate
    },
    dependency: [startDate, endDate],
    skip: !canViewInvoices,
    onSuccess: (response: any) => {
      setInvoiceFinancialKpis({
        totalAmountDue: response.total_amount_dues ?? 0,
        totalInvoiceAmount: response.total_invoice_amount ?? 0,
        totalCommission: response.total_commission ?? 0,
      });
      setInvoiceKpis({
        draft: response.total_draft_invoices ?? 0,
        sent: response.total_sent_invoices ?? 0,
        completed: response.total_completed_invoices ?? 0,
      });
    },
  });

  const isLoading = loadingTransactions || loadingReservations || loadingInvoices;

  return (
    <>
      <div className="row mb-4 align-items-center">
        <div className="col-sm-6">
          <h5 className="m-0 fw-bold">{t('layout.pageTexts.dashboard.pageTexts.currentMonthStats.monthStatistics')}</h5>
        </div>
        <div className="col-sm-6 d-flex justify-content-end">
           <div style={{ width: '250px' }}>
             <Dropdown
               values={[currentMonthYear]}
               options={monthOptions}
               allOptions={monthOptions}
               onChange={onMonthYearChange}
               id="month-year-filter"
               name="monthYear"
               label=""
               placeholder="Select Month"
               disableClearable={true}
               noSearch={true}
             />
           </div>
        </div>
      </div>
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <Spinner />
        </div>
      ) : (
        <div className="row g-6 mb-6">
          <div className="col-sm-6 col-xl-6">
            {canViewInvoices && (
              <CombinedInvoiceCard
                totalAmountDue={invoiceFinancialKpis.totalAmountDue}
                totalInvoiceAmount={invoiceFinancialKpis.totalInvoiceAmount}
                totalCommission={invoiceFinancialKpis.totalCommission}
                totalDraftInvoices={invoiceKpis.draft}
                totalSentInvoices={invoiceKpis.sent}
                totalCompletedInvoices={invoiceKpis.completed}
              />
            )}
          </div>
          <div className="col-sm-6 col-xl-6 d-flex flex-column gap-4">
            {canViewReservations && (
              <MonthStatCard
                title={t('layout.pageTexts.dashboard.pageTexts.currentMonthStats.titles.reservations')}
                totalRecords={totalReservations ?? 0}
                statItems={[
                  {
                    icon: 'ri ri-bar-chart-line',
                    value: reservationKpis.total,
                    label: 'total',
                    variant: 'danger'
                  },
                  {
                    icon: 'ri ri-file-edit-line',
                    value: reservationKpis.unbilled,
                    label: 'unbilled',
                    variant: 'primary'
                  },
                  {
                    icon: 'ri ri-file-list-line',
                    value: reservationKpis.invoiced,
                    label: 'invoiced',
                    variant: 'success'
                  },
                  {
                    icon: 'ri ri-checkbox-circle-line',
                    value: reservationKpis.settled,
                    label: 'settled',
                    variant: 'info'
                  }
                ]}
              />
            )}
            {canViewTransactions && (
              <MonthStatCard
                title={t('layout.pageTexts.dashboard.pageTexts.currentMonthStats.titles.transactions')}
                totalRecords={totalTransactions ?? 0}
                statItems={[
                  {
                    icon: 'ri ri-bar-chart-line',
                    value: transactionKpis.total,
                    label: 'total',
                    variant: 'danger'
                  },
                  {
                    icon: 'ri ri-file-edit-line',
                    value: transactionKpis.unbilled,
                    label: 'unbilled',
                    variant: 'primary'
                  },
                  {
                    icon: 'ri ri-file-list-line',
                    value: transactionKpis.invoiced,
                    label: 'invoiced',
                    variant: 'success'
                  },
                  {
                    icon: 'ri ri-checkbox-circle-line',
                    value: transactionKpis.settled,
                    label: 'settled',
                    variant: 'info'
                  }
                ]}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
export default CurrentMonthStats;

